import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getCatalog } from '../../api/catalog.service';

export const fetchAllDecks = createAsyncThunk(
  "catalog/fetchAllDecks",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCatalog({ type: 'deck', take: 5000 });
      return response?.data?.results || response?.data || [];
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// Helpers para la deduplicación del listado paginado (idénticos a los de la vista)
function isMyDecksCategory(group) {
  const title = (group?.categoryTitle || '').trim().toLowerCase();
  return title === 'my decks';
}

function getItemId(item) {
  const id = item?.sourceId ?? item?.catalogRef?.split(':')?.[1] ?? item?._id;
  return id != null ? String(id) : '';
}

function deduplicateCatalogItems(categories) {
  if (!categories?.length) return categories;
  const seen = new Set();
  return categories.map((cat) => ({
    ...cat,
    subcategories: (cat.subcategories || []).map((sub) => ({
      ...sub,
      items: (sub.items || []).filter((item) => {
        const id = getItemId(item);
        if (!id) return true;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      }),
    })),
  }));
}

export const fetchInitialCatalog = createAsyncThunk(
  "catalog/fetchInitialCatalog",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getCatalog({
        groupedBy: 'category',
        take: 5,
        page: 0,
        order: 'name',
        itemsPerCategory: 50,
        includeEmptyCategories: true,
      });
      if (res?.response?.status >= 400) {
        return rejectWithValue(res?.response?.data || "Error");
      }
      const raw = res?.data?.categories || [];
      const filtered = raw.filter((g) => !isMyDecksCategory(g));
      const incoming = deduplicateCatalogItems(filtered);
      const pagination = res?.data?.pagination || {};
      const totalPages = pagination.pages ?? 0;
      
      const expanded = new Set();
      incoming.forEach((cat) => {
        if ((cat.subcategories || []).some((s) => (s.items || []).length > 0)) {
          expanded.add(cat.categoryId);
        }
      });
      
      return { incoming, totalPages, currentPage: 0, expandedCategoryIds: Array.from(expanded) };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchAllTexts = createAsyncThunk(
  "catalog/fetchAllTexts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCatalog({ type: 'text', take: 5000 });
      return response?.data?.results || response?.data || [];
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

/**
 * Persists catalog list state (loaded data, pagination, expanded categories, scroll)
 * so that when the user navigates away and back (without refreshing), the catalog
 * is restored. State is cleared when filters change or on full page refresh.
 */
const catalogSlice = createSlice({
  name: 'catalog',
  initialState: {
    filtersKey: '',
    categoryGroups: [],
    totalPages: 0,
    currentPage: -1,
    expandedCategoryIds: [],
    expandedSubcategoryIds: [],
    newContentItems: [],
    scrollPosition: 0,
    allDecks: [],
    allTexts: [],
    isLoadingDecks: false,
    isLoadingTexts: false,
    catalogNeedsRefresh: false,
  },
  reducers: {
    setCatalogSnapshot: (state, action) => {
      const { filtersKey, categoryGroups, totalPages, currentPage, expandedCategoryIds, expandedSubcategoryIds, newContentItems, scrollPosition } = action.payload;
      if (filtersKey !== undefined) state.filtersKey = filtersKey;
      if (categoryGroups !== undefined) state.categoryGroups = categoryGroups;
      if (totalPages !== undefined) state.totalPages = totalPages;
      if (currentPage !== undefined) state.currentPage = currentPage;
      if (expandedCategoryIds !== undefined) state.expandedCategoryIds = expandedCategoryIds;
      if (expandedSubcategoryIds !== undefined) state.expandedSubcategoryIds = expandedSubcategoryIds;
      if (newContentItems !== undefined) state.newContentItems = newContentItems;
      if (scrollPosition !== undefined) state.scrollPosition = scrollPosition;
    },
    setCatalogScroll: (state, action) => {
      state.scrollPosition = action.payload;
    },
    clearCatalogState: (state) => {
      state.filtersKey = '';
      state.categoryGroups = [];
      state.totalPages = 0;
      state.currentPage = -1;
      state.expandedCategoryIds = [];
      state.expandedSubcategoryIds = [];
      state.newContentItems = [];
      state.scrollPosition = 0;
      state.catalogNeedsRefresh = false;
    },
    /** Add a newly created deck/text to the catalog snapshot so it appears when returning from add flow. */
    addCatalogItem: (state, action) => {
      const { item, categoryId, subcategoryId, type, categoryTitle, subcategoryTitle } = action.payload;
      if (!item?._id || !categoryId || !subcategoryId || !type) return;
      const catId = String(categoryId);
      const subId = String(subcategoryId);
      const catalogItem = {
        sourceId: item._id,
        catalogRef: `${type}:${item._id}`,
        type,
        title: item.title || (item.titleTranslations?.[0]?.trans) || '',
        titleTranslations: item.titleTranslations || item.translations,
        level: item.level,
        premium: item.premium,
      };
      state.categoryGroups = state.categoryGroups || [];
      let cat = state.categoryGroups.find((c) => String(c.categoryId) === catId);
      let sub = cat?.subcategories?.find((s) => String(s.subCategoryId ?? s._id) === subId);
      if (sub) {
        sub.items = sub.items || [];
        const exists = sub.items.some((i) => String(i.sourceId ?? i.catalogRef?.split(':')?.[1]) === String(item._id));
        if (!exists) sub.items.push(catalogItem);
      } else if (cat) {
        // Category exists but subcategory not found - create subcategory with item
        cat.subcategories = cat.subcategories || [];
        cat.subcategories.push({
          subCategoryId: subId,
          _id: subId,
          subCategoryTitle: subcategoryTitle || subId,
          items: [catalogItem],
        });
        // Ensure category and new subcategory are expanded when restored
        state.expandedCategoryIds = state.expandedCategoryIds || [];
        if (!state.expandedCategoryIds.includes(catId)) state.expandedCategoryIds.push(catId);
        state.expandedSubcategoryIds = state.expandedSubcategoryIds || [];
        const subKey = `${catId}:${subId}`;
        if (!state.expandedSubcategoryIds.includes(subKey)) state.expandedSubcategoryIds.push(subKey);
      } else {
        // Neither category nor subcategory in loaded groups - create both
        const newSub = {
          subCategoryId: subId,
          _id: subId,
          subCategoryTitle: subcategoryTitle || subId,
          items: [catalogItem],
        };
        state.categoryGroups.push({
          categoryId: catId,
          categoryTitle: categoryTitle || catId,
          subcategories: [newSub],
        });
        // Ensure new category/subcategory are expanded when restored
        state.expandedCategoryIds = state.expandedCategoryIds || [];
        if (!state.expandedCategoryIds.includes(catId)) state.expandedCategoryIds.push(catId);
        state.expandedSubcategoryIds = state.expandedSubcategoryIds || [];
        const subKey = `${catId}:${subId}`;
        if (!state.expandedSubcategoryIds.includes(subKey)) state.expandedSubcategoryIds.push(subKey);
      }
      if (type === 'deck') {
        state.allDecks = state.allDecks || [];
        if (!state.allDecks.some((d) => String(d._id ?? d.sourceId) === String(item._id))) {
          state.allDecks.unshift({ ...item, sourceId: item._id });
        }
      } else if (type === 'text') {
        state.allTexts = state.allTexts || [];
        if (!state.allTexts.some((t) => String(t._id ?? t.sourceId) === String(item._id))) {
          state.allTexts.unshift({ ...item, sourceId: item._id });
        }
      }
    },
    clearCatalogNeedsRefresh: (state) => {
      state.catalogNeedsRefresh = false;
    },
    /** Remove a catalog item from local state (after API delete). No API call. */
    removeCatalogItem: (state, action) => {
      const { sourceId, type } = action.payload;
      if (!sourceId) return;
      const id = String(sourceId);
      state.categoryGroups = (state.categoryGroups || []).map((cat) => ({
        ...cat,
        subcategories: (cat.subcategories || []).map((sub) => ({
          ...sub,
          items: (sub.items || []).filter((item) => {
            const itemId = String(item?.sourceId ?? item?.catalogRef?.split(':')?.[1] ?? item?._id ?? '');
            if (itemId !== id) return true;
            if (!type) return false;
            const itemType = String(item?.type ?? '');
            if (!itemType) return false;
            return itemType !== String(type);
          }),
        })),
      }));
      state.newContentItems = (state.newContentItems || []).filter(
        (item) => String(item?.sourceId ?? item?.catalogRef?.split(':')?.[1] ?? item?._id ?? '') !== id
      );
      if (!type || type === 'deck') {
        state.allDecks = (state.allDecks || []).filter((d) => String(d._id ?? d.sourceId) !== id);
      }
      if (!type || type === 'text') {
        state.allTexts = (state.allTexts || []).filter((t) => String(t._id ?? t.sourceId) !== id);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllDecks.pending, (state) => {
        state.isLoadingDecks = true;
      })
      .addCase(fetchAllDecks.fulfilled, (state, action) => {
        state.isLoadingDecks = false;
        state.allDecks = action.payload;
      })
      .addCase(fetchAllDecks.rejected, (state) => {
        state.isLoadingDecks = false;
      })
      .addCase(fetchAllTexts.pending, (state) => {
        state.isLoadingTexts = true;
      })
      .addCase(fetchAllTexts.fulfilled, (state, action) => {
        state.isLoadingTexts = false;
        state.allTexts = action.payload;
      })
      .addCase(fetchAllTexts.rejected, (state) => {
        state.isLoadingTexts = false;
      })
      .addCase(fetchInitialCatalog.fulfilled, (state, action) => {
        // Sólo cargamos la precarga si el usuario no ha navegado manual ni ha seteado ya los filtros.
        if (!state.categoryGroups || state.categoryGroups.length === 0) {
          state.categoryGroups = action.payload.incoming;
          state.totalPages = action.payload.totalPages;
          state.currentPage = action.payload.currentPage;
          state.expandedCategoryIds = action.payload.expandedCategoryIds;
          state.filtersKey = '';
        }
      });
  },
});

export const { setCatalogSnapshot, setCatalogScroll, clearCatalogState, addCatalogItem, removeCatalogItem, clearCatalogNeedsRefresh } = catalogSlice.actions;
export default catalogSlice.reducer;
